import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { LANE_WORKFLOWS, LANE_BY_KEY, CONVERTIBLE_TARGETS, laneStageLabel, firstStage } from "@/config/serviceLaneWorkflows";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Home, Building2, ShieldCheck, HousePlus, PackageOpen, HeartHandshake,
  Route as RouteIcon, Plus, Save, Trash2, ArrowRightLeft, Check, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const ICONS = { Home, Building2, ShieldCheck, HousePlus, PackageOpen, HeartHandshake };

const LaneIcon = ({ name, ...props }) => {
  const I = ICONS[name] || RouteIcon;
  return <I {...props} />;
};

/* ---- Field renderer ---- */
const FieldInput = ({ field, value, onChange, staff, disabled }) => {
  const common = { disabled, "data-testid": `lane-field-${field.key}` };
  if (field.type === "textarea") {
    return <Textarea rows={2} value={value || ""} onChange={(e) => onChange(e.target.value)} {...common} />;
  }
  if (field.type === "switch") {
    return (
      <div className="flex items-center h-9">
        <Switch checked={!!value} onCheckedChange={(v) => onChange(v)} disabled={disabled} data-testid={`lane-field-${field.key}`} />
        <span className="ml-2 text-xs text-muted-foreground">{value ? "Yes" : "No"}</span>
      </div>
    );
  }
  if (field.type === "select" || field.type === "staff") {
    const options = field.type === "staff"
      ? (staff || []).map((s) => ({ value: s.id, label: s.full_name }))
      : (field.options || []).map((o) => (typeof o === "string" ? { value: o, label: o } : o));
    return (
      <Select value={value || ""} onValueChange={(v) => onChange(v)} disabled={disabled}>
        <SelectTrigger data-testid={`lane-field-${field.key}`}><SelectValue placeholder="Select" /></SelectTrigger>
        <SelectContent className="max-h-[260px]">
          {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }
  // text | number | currency | date
  const type = field.type === "currency" || field.type === "number" ? "number" : (field.type === "date" ? "date" : "text");
  return (
    <div className="relative">
      {field.type === "currency" && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>}
      <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={field.type === "currency" ? "pl-6" : ""} {...common} />
    </div>
  );
};

/* ---- Pipeline stepper ---- */
const PipelineStepper = ({ lane, current }) => {
  const idx = lane.pipeline.findIndex((s) => s.value === current);
  return (
    <div className="flex items-center gap-1 overflow-x-auto mmm-scroll pb-1" data-testid="lane-stepper">
      {lane.pipeline.map((s, i) => {
        const done = idx >= 0 && i < idx;
        const active = i === idx;
        return (
          <div key={s.value} className="flex items-center gap-1 shrink-0">
            <div className="h-2 w-2 rounded-full" style={{ backgroundColor: active ? lane.accent : done ? "rgba(37,107,75,0.7)" : "#E6EDF2" }} title={s.label} />
            {i < lane.pipeline.length - 1 && <div className="h-px w-4" style={{ backgroundColor: done ? "rgba(37,107,75,0.4)" : "#E6EDF2" }} />}
          </div>
        );
      })}
    </div>
  );
};

/* ---- A single lane card ---- */
const LaneCard = ({ caseId, lane, workflow, staff, canWrite, onChange }) => {
  const [form, setForm] = useState(workflow.data || {});
  const [status, setStatus] = useState(workflow.status || "");
  const [savingDetails, setSavingDetails] = useState(false);
  const [convert, setConvert] = useState([]);
  const [converting, setConverting] = useState(false);

  // Initialize once per workflow id so reloads don't wipe in-progress edits.
  useEffect(() => { setForm(workflow.data || {}); setStatus(workflow.status || ""); }, [workflow.id]); // eslint-disable-line

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const saveStatus = async (v) => {
    setStatus(v);
    try { await api.put(`/cases/${caseId}/lanes/${workflow.id}`, { status: v }); toast.success(`${lane.label}: ${laneStageLabel(lane.key, v)}`); onChange(); }
    catch { toast.error("Could not update stage"); }
  };
  const saveDetails = async () => {
    setSavingDetails(true);
    try { await api.put(`/cases/${caseId}/lanes/${workflow.id}`, { data: form }); toast.success(`${lane.label} details saved`); onChange(); }
    catch { toast.error("Could not save details"); } finally { setSavingDetails(false); }
  };
  const remove = async () => {
    try { await api.delete(`/cases/${caseId}/lanes/${workflow.id}`); toast.success(`${lane.label} removed`); onChange(); }
    catch { toast.error("Could not remove lane"); }
  };
  const toggleConvert = (k) => setConvert((p) => p.includes(k) ? p.filter((x) => x !== k) : [...p, k]);
  const runConvert = async () => {
    if (convert.length === 0) { toast.error("Choose at least one service lane"); return; }
    setConverting(true);
    try {
      await api.post(`/cases/${caseId}/lanes/${workflow.id}/convert`, { lane_types: convert });
      toast.success("Converted into service lanes");
      setConvert([]); onChange();
    } catch { toast.error("Could not convert"); } finally { setConverting(false); }
  };

  return (
    <div className="space-y-4 pt-1">
      {/* Pipeline */}
      <div>
        <Label className="mb-1.5 block text-[13px]">Pipeline stage</Label>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <Select value={status || ""} onValueChange={saveStatus} disabled={!canWrite || !workflow.is_active}>
            <SelectTrigger className="w-full sm:w-[300px]" data-testid="lane-stage-select"><SelectValue placeholder="Set stage" /></SelectTrigger>
            <SelectContent className="max-h-[320px]">{lane.pipeline.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
          <PipelineStepper lane={lane} current={status} />
        </div>
      </div>

      {/* Fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {lane.fields.map((f) => (
          <div key={f.key} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
            <Label className="mb-1.5 block text-[13px]">{f.label}</Label>
            <FieldInput field={f} value={form[f.key]} onChange={(v) => setField(f.key, v)} staff={staff} disabled={!canWrite} />
          </div>
        ))}
      </div>

      {/* Convert panel for the Overwhelmed lane */}
      {lane.convertible && workflow.is_active && (
        <Card className="p-3 border-dashed border-[#9BB9CE] bg-[rgba(155,185,206,0.10)]" data-testid="convert-panel">
          <div className="flex items-center gap-2 mb-2">
            <ArrowRightLeft size={15} className="text-[#1C4358]" />
            <span className="text-sm font-semibold text-[#252221]">Convert into service lanes</span>
          </div>
          <p className="text-xs text-muted-foreground mb-2">Pick the lanes this family actually needs. We'll create each workflow and route this intake.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {CONVERTIBLE_TARGETS.map((t) => (
              <label key={t.key} className="flex items-center gap-2 text-sm cursor-pointer rounded-lg border border-[#E6EDF2] px-3 py-2 bg-white" data-testid={`convert-option-${t.key}`}>
                <Checkbox checked={convert.includes(t.key)} onCheckedChange={() => toggleConvert(t.key)} disabled={!canWrite} />
                <LaneIcon name={t.icon} size={14} style={{ color: t.accent }} /> {t.label}
              </label>
            ))}
          </div>
          <div className="flex justify-end mt-3">
            <Button size="sm" onClick={runConvert} disabled={!canWrite || converting} data-testid="run-convert-button" className="text-white" style={{ backgroundColor: "var(--mmm-navy)" }}>
              <ArrowRightLeft size={14} className="mr-1.5" /> {converting ? "Converting..." : `Convert (${convert.length})`}
            </Button>
          </div>
        </Card>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between gap-2 pt-1 border-t border-[#E6EDF2] mt-1">
        {canWrite ? (
          <Button variant="ghost" size="sm" onClick={remove} className="text-[#9C1E22] hover:bg-[rgba(156,30,34,0.08)]" data-testid="remove-lane-button">
            <Trash2 size={14} className="mr-1.5" /> Remove lane
          </Button>
        ) : <span />}
        {canWrite && workflow.is_active && (
          <Button size="sm" onClick={saveDetails} disabled={savingDetails} data-testid="save-lane-details-button" className="text-white" style={{ backgroundColor: "var(--mmm-red)" }}>
            <Save size={14} className="mr-1.5" /> {savingDetails ? "Saving..." : "Save details"}
          </Button>
        )}
      </div>
    </div>
  );
};

/* ---- Main component ---- */
export default function ServiceLaneWorkflows({ caseId, staff, workflows, canWrite, onChange }) {
  const [addOpen, setAddOpen] = useState(false);
  const [adding, setAdding] = useState("");
  const list = (workflows || []).filter((w) => !w.is_deleted);
  const activeTypes = new Set(list.filter((w) => w.is_active).map((w) => w.lane_type));
  const available = LANE_WORKFLOWS.filter((l) => !activeTypes.has(l.key));

  const addLane = async (laneKey) => {
    setAdding(laneKey);
    try {
      await api.post(`/cases/${caseId}/lanes`, { lane_type: laneKey, status: firstStage(laneKey) });
      toast.success(`${LANE_BY_KEY[laneKey].label} added`);
      setAddOpen(false); onChange();
    } catch { toast.error("Could not add service lane"); } finally { setAdding(""); }
  };

  return (
    <Card className="p-4 sm:p-5 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid="service-lane-workflows">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div>
          <h3 className="font-display font-semibold text-[#252221] flex items-center gap-2"><RouteIcon size={17} className="text-[#1C4358]" /> Active service lanes</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Run each specialized workflow in parallel for this family.</p>
        </div>
        {canWrite && (
          <Button size="sm" onClick={() => setAddOpen(true)} disabled={available.length === 0} data-testid="add-lane-button" className="text-white" style={{ backgroundColor: "var(--mmm-red)" }}>
            <Plus size={15} className="mr-1" /> Add service lane
          </Button>
        )}
      </div>

      {list.length === 0 ? (
        <div className="py-10 text-center" data-testid="lanes-empty">
          <div className="mx-auto mb-3 h-12 w-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: "rgba(28,67,88,0.08)" }}>
            <RouteIcon size={22} className="text-[#1C4358]" />
          </div>
          <p className="font-medium text-[#252221]">No service lanes yet</p>
          <p className="text-sm text-muted-foreground">Add a lane to start a specialized workflow — or start with "Not Sure / Overwhelmed".</p>
        </div>
      ) : (
        <Accordion type="single" collapsible defaultValue={list.find((w) => w.is_active)?.id} className="space-y-2">
          {list.map((w) => {
            const lane = LANE_BY_KEY[w.lane_type];
            if (!lane) return null;
            return (
              <AccordionItem key={w.id} value={w.id} className={`border rounded-xl px-3 ${w.is_active ? "border-[#E6EDF2] bg-white" : "border-dashed border-[#E6EDF2] bg-[#F7FAFC] opacity-80"}`} data-testid={`lane-item-${w.lane_type}`}>
                <AccordionTrigger className="hover:no-underline py-3" data-testid={`lane-trigger-${w.lane_type}`}>
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <span className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${lane.accent}1A` }}>
                      <LaneIcon name={lane.icon} size={16} style={{ color: lane.accent }} />
                    </span>
                    <div className="min-w-0 text-left">
                      <div className="font-medium text-[#252221] truncate">{lane.label}</div>
                      <div className="text-xs text-muted-foreground truncate">{laneStageLabel(lane.key, w.status)}</div>
                    </div>
                    {!w.is_active && (
                      <span className="ml-auto mr-2 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full shrink-0" style={{ backgroundColor: "rgba(75,85,99,0.12)", color: "#4B5563" }}>
                        <CheckCircle2 size={10} /> Routed
                      </span>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <LaneCard caseId={caseId} lane={lane} workflow={w} staff={staff} canWrite={canWrite} onChange={onChange} />
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}

      {/* Add lane dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg" data-testid="add-lane-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-lg">Add a service lane</DialogTitle>
            <DialogDescription>Choose a specialized workflow to run for this family.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {available.map((l) => (
              <button key={l.key} onClick={() => addLane(l.key)} disabled={!!adding}
                className="flex items-center gap-3 rounded-xl border border-[#E6EDF2] p-3 text-left hover:border-[#1C4358] hover:bg-[rgba(28,67,88,0.04)] transition-colors disabled:opacity-60"
                data-testid={`add-lane-option-${l.key}`}>
                <span className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${l.accent}1A` }}>
                  <LaneIcon name={l.icon} size={17} style={{ color: l.accent }} />
                </span>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[#252221]">{l.label}</div>
                  <div className="text-[11px] text-muted-foreground">{l.pipeline.length} stages</div>
                </div>
                {adding === l.key && <Check size={15} className="ml-auto text-[#256B4B]" />}
              </button>
            ))}
            {available.length === 0 && <p className="text-sm text-muted-foreground col-span-2 py-2">All service lanes are already active on this case.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
